import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Stack,
  Divider,
  Chip,
  IconButton,
  alpha,
} from "@mui/material";
import { useFormik } from "formik";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  useCreateClassScheduleMutation,
  useCheckClassScheduleConflictsMutation,
  useGetallStudentQuery,
  useGetallGradeQuery,
  useGetallSubjectQuery,
  useGetAssignClassListFilterQuery,
  useGetClassScheduleFilterOptionsQuery,
} from "../../../Api/attoDeskApi";
import { normalizeGradeOptions, normalizeSubjectOptions, subjectsForGrades } from "../List/filterOptions";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import {
  DatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import PremiumTimePicker from "../../../Components/PremiumTimePicker";
import AvailableStaffPicker from "./AvailableStaffPicker";

// HH:mm helpers (shared time fields now use "HH:mm" strings, not Dayjs).
const _toMin = (t: string) => { const [h, m] = String(t || "").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const _hhmmTo12 = (t: string) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; const h12 = h % 12 || 12; return `${h12}:${String(m || 0).padStart(2, "0")} ${ap}`; };

// ── Field Label ──
const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> =
  ({ children, required }) => (
    <Typography
      sx={{
        fontSize: 11.5,
        fontWeight: 600,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        mb: 0.75,
      }}
    >
      {children}
      {required && (
        <Box component="span" sx={{ color: "#ef4444", ml: 0.25 }}>
          *
        </Box>
      )}
    </Typography>
  );

// ── Section Header ──
const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  color?: string;
}> = ({ icon, title, color = "#6366f1" }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "8px",
        bgcolor: alpha(color, 0.08),
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& svg": { fontSize: 17 },
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{title}</Typography>
  </Box>
);

// ── Summary Row ──
const SummaryRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  color?: string;
}> = ({ icon, label, value, color }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      py: 1.25,
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "7px",
        bgcolor: alpha(color || "#6366f1", 0.08),
        color: color || "#6366f1",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        "& svg": { fontSize: 15 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          color: "text.disabled",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: value && value !== "—" ? "text.primary" : "text.disabled",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  </Box>
);

// ── Schedule Type Pill ──
const TypePill: React.FC<{
  label: string;
  selected: boolean;
  onClick: () => void;
}> = ({ label, selected, onClick }) => {
  const theme = {
    main: "#6366f1",
  };
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 0.75,
        borderRadius: "20px",
        fontSize: 12.5,
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: `1.5px solid ${selected ? theme.main : "rgba(0,0,0,0.12)"}`,
        bgcolor: selected ? alpha(theme.main, 0.08) : "transparent",
        color: selected ? theme.main : "text.secondary",
        textTransform: "capitalize",
        "&:hover": {
          borderColor: theme.main,
          bgcolor: alpha(theme.main, 0.04),
        },
      }}
    >
      {label}
    </Box>
  );
};

const SCHEDULE_TYPES = [
  { value: "regular", label: "Regular" },
  { value: "makeup", label: "Makeup" },
  { value: "trial", label: "Trial" },
  { value: "extra", label: "Extra" },
];

const Index = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();

  const [studentSearch, setStudentSearch] = useState("");
  const debouncedStudentSearch = useDebounce(studentSearch, 300);

  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [pendingValues, setPendingValues] = useState<any>(null);

  const [createSchedule, { isLoading }] =
    useCreateClassScheduleMutation();
  const [checkConflicts, { isLoading: isCheckingConflicts }] =
    useCheckClassScheduleConflictsMutation();

  const { data: studentData } = useGetallStudentQuery({
    searchText: debouncedStudentSearch,
    id: "",
  });
  const { data: gradeData } = useGetallGradeQuery({ searchText: "" });
  const { data: subjectData } = useGetallSubjectQuery({ searchText: "" });
  // Grade↔Subject mapping — single fetch, so Subject dependency needs no extra
  // per-selection round trip. `grade_subjects` gives [{id, name, grade_ids[]}].
  const { data: filterOptionsData, isFetching: subjectsLoading } =
    useGetClassScheduleFilterOptionsQuery();
  const gradeOptionsFO = useMemo(
    () => normalizeGradeOptions((filterOptionsData as any)?.data),
    [filterOptionsData]
  );
  const subjectOptionsAll = useMemo(
    () => normalizeSubjectOptions((filterOptionsData as any)?.data),
    [filterOptionsData]
  );

  // Resolve assign_classes rows for the selected staff so we can derive `classId`.
  const [staffIdForLookup, setStaffIdForLookup] = useState<string>("");
  const { data: assignClassListData } = useGetAssignClassListFilterQuery(
    {
      id: "",
      staff_id: staffIdForLookup,
      location_id: "",
      class_mode: "",
      class_type: "",
      select_grade_subject_ids: [],
      selected_days: [],
    } as any,
    { skip: !staffIdForLookup }
  );
  const assignClassList: any[] = useMemo(
    () => (Array.isArray(assignClassListData?.data) ? (assignClassListData!.data as any[]) : []),
    [assignClassListData]
  );

  const studentList = useMemo(
    () =>
      Array.isArray(studentData?.data) ? (studentData?.data as any[]) : [],
    [studentData]
  );
  const gradeList = useMemo(
    () => (Array.isArray(gradeData?.data) ? (gradeData?.data as any[]) : []),
    [gradeData]
  );
  const subjectList = useMemo(
    () =>
      Array.isArray(subjectData?.data) ? (subjectData?.data as any[]) : [],
    [subjectData]
  );

  // Prefer the mapping-aware grade/subject list from filter-options; fall back
  // to the standalone lookups if the endpoint hasn't been redeployed yet.
  const gradeListForForm = useMemo(
    () => (gradeOptionsFO.length > 0
      ? gradeOptionsFO.map((g) => ({ id: g.id, grade: g.name }))
      : gradeList),
    [gradeOptionsFO, gradeList]
  );

  const formik = useFormik({
    initialValues: {
      studentId: "",
      studentName: "",
      staffId: "",
      staffName: "",
      subjectId: "",
      gradeId: "",
      scheduleDate: dayjs() as Dayjs | null,
      startTime: "09:00", // "HH:mm"
      endTime: "10:00",   // "HH:mm"
      scheduleType: "regular",
      feePerClass: "",
      notes: "",
    },
    onSubmit: async (values) => {
      try {
        if (!values.studentId || !values.staffId) {
          showErrorMessage("Please select both a student and a staff member.");
          return;
        }
        if (!values.gradeId) {
          showErrorMessage("Please select a grade.");
          return;
        }
        if (!values.subjectId) {
          const gName = selectedGradeName || "the selected grade";
          showErrorMessage(`Please select a subject for ${gName}.`);
          return;
        }
        // Guard against a stale pair (e.g. user opened the dropdown before the
        // pruning effect resolved). The backend re-validates the pair, but this
        // spares the user a round trip.
        if (subjectOptionsAll.length > 0 && !scopedSubjectIdSet.has(Number(values.subjectId))) {
          showErrorMessage("The selected subject is not available for this grade.");
          return;
        }
        if (!values.scheduleDate) {
          showErrorMessage("Please pick a scheduled date.");
          return;
        }

        // Resolve assign_classes.id ("classId") from the staff's class list.
        // Prefer the class that contains the chosen student; fall back to a
        // staff+subject+grade match; finally to any class for this staff.
        const studentIdNum = Number(values.studentId);
        const subjectIdNum = values.subjectId ? Number(values.subjectId) : null;
        const gradeIdNum = values.gradeId ? Number(values.gradeId) : null;

        const matchByStudent = assignClassList.find((c: any) =>
          (c.assignStudent || c.assign_student || []).some((s: any) => {
            const sid = s.student_id ?? s.studentId ?? s.student?.id;
            return Number(sid) === studentIdNum;
          })
        );
        const matchBySubjectGrade = !matchByStudent
          ? assignClassList.find((c: any) => {
              const ids = c.select_grade_subject_ids || [];
              return (
                (!subjectIdNum || ids.includes(subjectIdNum)) &&
                (!gradeIdNum || c.grade_id === gradeIdNum)
              );
            })
          : null;
        const resolvedClass = matchByStudent || matchBySubjectGrade || assignClassList[0];

        if (!resolvedClass?.id) {
          showErrorMessage(
            "No assigned class found for this staff. Create an assigned class first under Classes → Assign Classes."
          );
          return;
        }

        const payload = {
          classId: resolvedClass.id,
          studentId: values.studentId,
          staffId: values.staffId,
          subjectId: values.subjectId,
          gradeId: values.gradeId,
          scheduledDate: values.scheduleDate
            ? values.scheduleDate.format("YYYY-MM-DD")
            : "",
          startTime: values.startTime || "",
          endTime: values.endTime || "",
          scheduleType: values.scheduleType,
          feePerClass: values.feePerClass
            ? parseFloat(values.feePerClass)
            : 0,
          notes: values.notes,
        };

        const conflictResponse = await checkConflicts(payload).unwrap();
        if (
          conflictResponse.status &&
          conflictResponse.data &&
          Array.isArray(conflictResponse.data) &&
          conflictResponse.data.length > 0
        ) {
          setConflicts(conflictResponse.data);
          setPendingValues(payload);
          setConflictDialogOpen(true);
          return;
        }

        await submitSchedule(payload);
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  // ── Grade → Subject dependency ─────────────────────────────────────────────
  // Subject options are strictly scoped to the selected Grade using the real
  // subjects_grades_tables pivot returned by the filter-options endpoint. Empty
  // grade => empty options (field stays disabled). No global flattened list.
  const currentGradeId = Number(formik.values.gradeId) || 0;
  const scopedSubjectOptions = useMemo(() => {
    if (!currentGradeId) return [];
    if (subjectOptionsAll.length === 0) return [];
    return subjectsForGrades(subjectOptionsAll, [currentGradeId]);
  }, [currentGradeId, subjectOptionsAll]);
  const scopedSubjectIdSet = useMemo(
    () => new Set(scopedSubjectOptions.map((s) => s.id)),
    [scopedSubjectOptions]
  );

  // When Grade changes, drop a stale Subject (§3 spec). Preserve when the same
  // subject is genuinely mapped to the new grade. Also clear the selected
  // Staff — their eligibility (grade+subject pair) is no longer guaranteed and
  // AvailableStaffPicker will refetch on the change.
  const prevGradeIdRef = React.useRef<number>(currentGradeId);
  React.useEffect(() => {
    const prev = prevGradeIdRef.current;
    if (prev === currentGradeId) return;
    prevGradeIdRef.current = currentGradeId;
    // No grade → clear subject and staff.
    if (!currentGradeId) {
      if (formik.values.subjectId) formik.setFieldValue("subjectId", "");
      if (formik.values.staffId) { formik.setFieldValue("staffId", ""); formik.setFieldValue("staffName", ""); }
      return;
    }
    // Grade present — only prune subject when the mapping has actually loaded.
    if (subjectOptionsAll.length > 0 && formik.values.subjectId) {
      const sid = Number(formik.values.subjectId);
      if (!scopedSubjectIdSet.has(sid)) {
        formik.setFieldValue("subjectId", "");
      }
    }
    // Any grade change invalidates the staff pair-qualification.
    if (formik.values.staffId) {
      formik.setFieldValue("staffId", "");
      formik.setFieldValue("staffName", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGradeId, subjectOptionsAll]);

  // Subject change also invalidates staff — the grade+subject pair drives
  // eligibility, so a subject flip must not silently keep a stale staff pick.
  const prevSubjectIdRef = React.useRef<string>(formik.values.subjectId);
  React.useEffect(() => {
    const prev = prevSubjectIdRef.current;
    const next = formik.values.subjectId;
    if (prev === next) return;
    prevSubjectIdRef.current = next;
    if (formik.values.staffId) {
      formik.setFieldValue("staffId", "");
      formik.setFieldValue("staffName", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.subjectId]);

  // Selected student's billing type — drives whether the per-class fee input
  // should be shown. "monthly" students are billed by recurring invoice, not
  // per session, so the fee field is irrelevant for them.
  const selectedStudent = useMemo(
    () => studentList.find((s: any) => String(s.id) === String(formik.values.studentId || "")),
    [studentList, formik.values.studentId]
  );
  const isMonthlyBilled = (selectedStudent?.billing_type || "").toLowerCase() === "monthly";

  // Clear any stale per-class fee when the student is monthly-billed.
  React.useEffect(() => {
    if (isMonthlyBilled && formik.values.feePerClass !== "") {
      formik.setFieldValue("feePerClass", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMonthlyBilled]);

  const submitSchedule = async (payload: any) => {
    try {
      const response = await createSchedule(payload).unwrap();
      if (response.status) {
        showMessage(response.message);
        formik.resetForm();
        navigate("/class-schedule/list");
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to create class schedule");
    }
  };


  const formValid = useMemo(() => {
    return (
      formik.values.studentId !== "" &&
      formik.values.gradeId !== "" &&
      formik.values.subjectId !== "" &&
      formik.values.staffId !== "" &&
      formik.values.scheduleDate !== null &&
      formik.values.startTime !== "" &&
      formik.values.endTime !== "" &&
      _toMin(formik.values.endTime) > _toMin(formik.values.startTime)
    );
  }, [formik.values]);

  // ── Derived display values ──
  const selectedSubjectName = useMemo(() => {
    if (!formik.values.subjectId) return "";
    const sid = Number(formik.values.subjectId);
    const fromScoped = subjectOptionsAll.find((x) => x.id === sid);
    if (fromScoped) return fromScoped.name;
    const s = subjectList.find((x: any) => x.id === sid);
    return s?.subject_name || "";
  }, [formik.values.subjectId, subjectOptionsAll, subjectList]);

  const selectedGradeName = useMemo(() => {
    if (!formik.values.gradeId) return "";
    const g = gradeListForForm.find(
      (x: any) => x.id === Number(formik.values.gradeId)
    );
    return g?.grade || "";
  }, [formik.values.gradeId, gradeListForForm]);

  const durationText = useMemo(() => {
    if (!formik.values.startTime || !formik.values.endTime) return "";
    const diff = _toMin(formik.values.endTime) - _toMin(formik.values.startTime);
    if (diff <= 0) return "Invalid";
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  }, [formik.values.startTime, formik.values.endTime]);

  const timeDisplay = useMemo(() => {
    const s = _hhmmTo12(formik.values.startTime);
    const e = _hhmmTo12(formik.values.endTime);
    if (s && e) return `${s} – ${e}`;
    return "—";
  }, [formik.values.startTime, formik.values.endTime]);

  const filledCount = useMemo(() => {
    let count = 0;
    if (formik.values.studentId) count++;
    if (formik.values.gradeId) count++;
    if (formik.values.subjectId) count++;
    if (formik.values.staffId) count++;
    if (formik.values.scheduleDate && formik.values.startTime && formik.values.endTime) count++;
    return count;
  }, [formik.values]);

  const inputSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "10px" },
  };

  return (
    <>
      {/* ════════════════════════════════════════════
          BACKDROP + CENTERED MODAL
         ════════════════════════════════════════════ */}
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1200,
          bgcolor: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 1, md: 3 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 920,
            maxHeight: "95vh",
            bgcolor: "#fff",
            borderRadius: "16px",
            boxShadow:
              "0 25px 50px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* ── HEADER ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2.5,
              borderBottom: "1px solid rgba(0,0,0,0.06)",
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                }}
              >
                Create Class Schedule
              </Typography>
              <Typography
                sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}
              >
                Schedule a new class session for a student
              </Typography>
            </Box>
            <IconButton
              onClick={() => navigate("/class-schedule/list")}
              sx={{
                width: 36,
                height: 36,
                bgcolor: "rgba(0,0,0,0.04)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* ── BODY: 2-Column ── */}
          <Box
            sx={{
              display: "flex",
              flex: 1,
              overflow: "hidden",
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {/* ──── LEFT SIDE: Form (65%) ──── */}
            <Box
              sx={{
                flex: { xs: 1, md: "0 0 63%" },
                overflowY: "auto",
                px: 3,
                py: 3,
                "&::-webkit-scrollbar": { width: 5 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "rgba(0,0,0,0.08)",
                  borderRadius: 3,
                },
              }}
            >
              <Stack spacing={4}>
                {/* Section 1: Student & Staff */}
                <Box>
                  <SectionHeader
                    icon={<PersonOutlineIcon />}
                    title="Student & Staff"
                    color="#6366f1"
                  />
                  <Stack spacing={2}>
                    <Box>
                      <FieldLabel required>Student</FieldLabel>
                      <Autocomplete
                        options={studentList}
                        getOptionLabel={(option: any) =>
                          `${option.first_name || ""} ${option.last_name || ""}`.trim()
                        }
                        onInputChange={(_, val) => setStudentSearch(val)}
                        onChange={(_, val) => {
                          formik.setFieldValue("studentId", val?.id || "");
                          formik.setFieldValue(
                            "studentName",
                            val
                              ? `${val.first_name} ${val.last_name}`
                              : ""
                          );
                          if (val?.grade_id) {
                            formik.setFieldValue(
                              "gradeId",
                              String(val.grade_id)
                            );
                          }
                        }}
                        renderOption={(props, option: any) => (
                          <Box
                            component="li"
                            {...props}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Avatar
                              src={option.image || undefined}
                              sx={{
                                width: 30,
                                height: 30,
                                fontSize: 13,
                                bgcolor: alpha("#6366f1", 0.12),
                                color: "#6366f1",
                              }}
                            >
                              {(option.first_name || "S")
                                .charAt(0)
                                .toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                                {option.first_name} {option.last_name}
                              </Typography>
                              {option.grade_id && (
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    color: "text.disabled",
                                  }}
                                >
                                  Grade {option.grade_id}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search student..."
                            size="small"
                            sx={inputSx}
                          />
                        )}
                      />
                    </Box>

                    <Box>
                      <FieldLabel required>Staff</FieldLabel>
                      {/* Availability-aware: only academically-suitable AND free
                          teachers are selectable; busy-but-suitable staff and real
                          alternative times are shown for guidance. Refetches when
                          grade/subject/date/time change and clears a stale pick. */}
                      <AvailableStaffPicker
                        gradeId={formik.values.gradeId}
                        subjectId={formik.values.subjectId}
                        studentId={formik.values.studentId}
                        date={formik.values.scheduleDate ? formik.values.scheduleDate.format("YYYY-MM-DD") : ""}
                        startTime={formik.values.startTime}
                        endTime={formik.values.endTime}
                        value={formik.values.staffId ? String(formik.values.staffId) : ""}
                        onChange={(staffId, staffName) => {
                          formik.setFieldValue("staffId", staffId);
                          formik.setFieldValue("staffName", staffName);
                          setStaffIdForLookup(staffId || "");
                        }}
                        onPickAlternative={(p) => {
                          formik.setFieldValue("startTime", p.start);
                          formik.setFieldValue("endTime", p.end);
                          formik.setFieldValue("staffId", String(p.staffId));
                          formik.setFieldValue("staffName", p.staffName);
                          setStaffIdForLookup(String(p.staffId));
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>

                <Divider />

                {/* Section 2: Class Details */}
                <Box>
                  <SectionHeader
                    icon={<MenuBookOutlinedIcon />}
                    title="Class Details"
                    color="#0ea5e9"
                  />
                  {/* Grade FIRST (spec §1) — Subject is dependent on it, so
                      putting Subject first would be misleading. Equal widths;
                      stacks vertically on mobile with Grade above Subject. */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                  >
                    <Box sx={{ flex: 1 }}>
                      <FieldLabel required>Grade</FieldLabel>
                      <FormControl fullWidth size="small">
                        <Select
                          value={formik.values.gradeId}
                          onChange={(e) =>
                            formik.setFieldValue("gradeId", e.target.value)
                          }
                          displayEmpty
                          sx={{ borderRadius: "10px" }}
                        >
                          <MenuItem value="">
                            <Typography
                              sx={{ fontSize: 13, color: "text.disabled" }}
                            >
                              Select Grade
                            </Typography>
                          </MenuItem>
                          {gradeListForForm.map((g: any) => (
                            <MenuItem key={g.id} value={g.id}>
                              {g.grade}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {/* Subject: disabled until Grade is picked (§2). While the
                        grade→subject mapping is fetching we show a loading hint
                        and keep the field stable. Empty grade mapping shows the
                        "No subjects configured" explanation rather than a blank
                        dropdown. */}
                    {(() => {
                      const gradeChosen = !!formik.values.gradeId;
                      const isLoading = gradeChosen && subjectsLoading && subjectOptionsAll.length === 0;
                      const gradeHasNoSubjects =
                        gradeChosen && !isLoading && subjectOptionsAll.length > 0 && scopedSubjectOptions.length === 0;
                      const placeholder = !gradeChosen
                        ? "Select a grade first"
                        : isLoading
                          ? "Loading subjects…"
                          : gradeHasNoSubjects
                            ? `No subjects are configured for ${selectedGradeName || "this grade"}.`
                            : "Select Subject";
                      const disabled = !gradeChosen || isLoading || gradeHasNoSubjects;
                      return (
                        <Box sx={{ flex: 1 }}>
                          <FieldLabel required>Subject</FieldLabel>
                          <FormControl fullWidth size="small" disabled={disabled}>
                            <Select
                              value={formik.values.subjectId}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "subjectId",
                                  e.target.value
                                )
                              }
                              displayEmpty
                              sx={{ borderRadius: "10px" }}
                            >
                              <MenuItem value="">
                                <Typography
                                  sx={{ fontSize: 13, color: "text.disabled" }}
                                >
                                  {placeholder}
                                </Typography>
                              </MenuItem>
                              {scopedSubjectOptions.map((s) => (
                                <MenuItem key={s.id} value={s.id}>
                                  {s.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {gradeHasNoSubjects && (
                            <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.5 }}>
                              {`No subjects are configured for ${selectedGradeName}.`}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </Stack>
                </Box>

                <Divider />

                {/* Section 3: Date & Time */}
                <Box>
                  <SectionHeader
                    icon={<CalendarTodayOutlinedIcon />}
                    title="Date & Time"
                    color="#22c55e"
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack spacing={2}>
                      <Box>
                        <FieldLabel required>Date</FieldLabel>
                        <DatePicker
                          value={formik.values.scheduleDate}
                          onChange={(val) =>
                            formik.setFieldValue("scheduleDate", val)
                          }
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              sx: inputSx,
                            },
                          }}
                        />
                      </Box>
                      <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <FieldLabel required>Start Time</FieldLabel>
                          <PremiumTimePicker
                            label=""
                            value={formik.values.startTime}
                            onChange={(v) => {
                              // Preserve duration when the start shifts.
                              const dur = formik.values.startTime && formik.values.endTime
                                ? _toMin(formik.values.endTime) - _toMin(formik.values.startTime) : 0;
                              formik.setFieldValue("startTime", v);
                              if (v && dur > 0) {
                                const em = (_toMin(v) + dur) % 1440;
                                formik.setFieldValue("endTime", `${String(Math.floor(em / 60)).padStart(2, "0")}:${String(em % 60).padStart(2, "0")}`);
                              }
                            }}
                            clearable={false}
                            sx={inputSx}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <FieldLabel required>End Time</FieldLabel>
                          <PremiumTimePicker
                            label=""
                            value={formik.values.endTime}
                            error={!!formik.values.startTime && !!formik.values.endTime && _toMin(formik.values.endTime) <= _toMin(formik.values.startTime)}
                            onChange={(v) => formik.setFieldValue("endTime", v)}
                            clearable={false}
                            sx={inputSx}
                          />
                        </Box>
                      </Stack>
                      {/* Duration display */}
                      {durationText && durationText !== "Invalid" && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: "8px",
                            bgcolor: alpha("#22c55e", 0.06),
                            border: `1px solid ${alpha("#22c55e", 0.12)}`,
                            width: "fit-content",
                          }}
                        >
                          <TimerOutlinedIcon
                            sx={{ fontSize: 15, color: "#22c55e" }}
                          />
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#16a34a",
                            }}
                          >
                            Duration: {durationText}
                          </Typography>
                        </Box>
                      )}
                      {durationText === "Invalid" && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: "8px",
                            bgcolor: alpha("#ef4444", 0.06),
                            border: `1px solid ${alpha("#ef4444", 0.12)}`,
                            width: "fit-content",
                          }}
                        >
                          <WarningAmberIcon
                            sx={{ fontSize: 15, color: "#ef4444" }}
                          />
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#ef4444",
                            }}
                          >
                            End time must be after start time
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </LocalizationProvider>
                </Box>

                <Divider />

                {/* Section 4: Settings */}
                <Box>
                  <SectionHeader
                    icon={<TuneOutlinedIcon />}
                    title="Settings"
                    color="#f59e0b"
                  />
                  <Stack spacing={2.5}>
                    <Box>
                      <FieldLabel>Schedule Type</FieldLabel>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {SCHEDULE_TYPES.map((t) => (
                          <TypePill
                            key={t.value}
                            label={t.label}
                            selected={formik.values.scheduleType === t.value}
                            onClick={() =>
                              formik.setFieldValue("scheduleType", t.value)
                            }
                          />
                        ))}
                      </Box>
                    </Box>
                    {isMonthlyBilled ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.5,
                          py: 1,
                          borderRadius: "10px",
                          bgcolor: alpha("#22c55e", 0.06),
                          border: `1px solid ${alpha("#22c55e", 0.18)}`,
                          maxWidth: 360,
                        }}
                      >
                        <CheckCircleOutlineIcon
                          sx={{ fontSize: 16, color: "#16a34a" }}
                        />
                        <Typography sx={{ fontSize: 12.5, color: "#15803d", fontWeight: 500 }}>
                          Billed monthly — no per-class fee needed.
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <FieldLabel>Fee Per Class</FieldLabel>
                        <TextField
                          placeholder="0.00"
                          size="small"
                          type="number"
                          fullWidth
                          {...formik.getFieldProps("feePerClass")}
                          InputProps={{
                            startAdornment: (
                              <Typography
                                sx={{
                                  mr: 0.5,
                                  fontSize: 14,
                                  color: "text.disabled",
                                  fontWeight: 600,
                                }}
                              >
                                $
                              </Typography>
                            ),
                            sx: { fontSize: 14 },
                          }}
                          sx={{ ...inputSx, maxWidth: 200 }}
                        />
                      </Box>
                    )}
                  </Stack>
                </Box>

                <Divider />

                {/* Section 5: Notes */}
                <Box>
                  <SectionHeader
                    icon={<NotesOutlinedIcon />}
                    title="Notes"
                    color="#8b5cf6"
                  />
                  <TextField
                    placeholder="Add any notes about this session..."
                    size="small"
                    multiline
                    rows={3}
                    fullWidth
                    {...formik.getFieldProps("notes")}
                    sx={inputSx}
                    InputProps={{ sx: { fontSize: 13 } }}
                  />
                </Box>
              </Stack>
            </Box>

            {/* ──── RIGHT SIDE: Live Summary (35%) ──── */}
            <Box
              sx={{
                flex: { xs: "none", md: "0 0 37%" },
                borderLeft: { md: "1px solid rgba(0,0,0,0.06)" },
                borderTop: { xs: "1px solid rgba(0,0,0,0.06)", md: "none" },
                bgcolor: "#fafbfc",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  p: 3,
                  flex: 1,
                  overflowY: "auto",
                  position: { md: "sticky" },
                  top: 0,
                }}
              >
                {/* Summary header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2.5,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "-0.01em" }}
                  >
                    Schedule Summary
                  </Typography>
                  <Chip
                    label={`${filledCount}/5`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor:
                        filledCount >= 5
                          ? alpha("#22c55e", 0.1)
                          : alpha("#6366f1", 0.08),
                      color: filledCount >= 5 ? "#22c55e" : "#6366f1",
                    }}
                  />
                </Box>

                {/* Summary rows */}
                <Stack
                  divider={
                    <Divider sx={{ borderColor: "rgba(0,0,0,0.04)" }} />
                  }
                >
                  {/* Order per spec §Schedule Summary: Student → Grade →
                      Subject → Staff → Date → Time → Duration → Fee. Grade
                      must appear before Subject in the summary too. */}
                  <SummaryRow
                    icon={<PersonOutlineIcon />}
                    label="Student"
                    value={formik.values.studentName}
                    color="#6366f1"
                  />
                  <SummaryRow
                    icon={<SchoolOutlinedIcon />}
                    label="Grade"
                    value={selectedGradeName}
                    color="#8b5cf6"
                  />
                  <SummaryRow
                    icon={<MenuBookOutlinedIcon />}
                    label="Subject"
                    value={selectedSubjectName}
                    color="#f59e0b"
                  />
                  <SummaryRow
                    icon={<PersonOutlineIcon />}
                    label="Staff"
                    value={formik.values.staffName}
                    color="#0ea5e9"
                  />
                  <SummaryRow
                    icon={<CalendarTodayOutlinedIcon />}
                    label="Date"
                    value={
                      formik.values.scheduleDate
                        ? formik.values.scheduleDate.format("ddd, MMM D, YYYY")
                        : ""
                    }
                    color="#22c55e"
                  />
                  <SummaryRow
                    icon={<ScheduleOutlinedIcon />}
                    label="Time"
                    value={timeDisplay}
                    color="#0ea5e9"
                  />
                  <SummaryRow
                    icon={<TimerOutlinedIcon />}
                    label="Duration"
                    value={
                      durationText && durationText !== "Invalid"
                        ? durationText
                        : ""
                    }
                    color="#14b8a6"
                  />
                  <SummaryRow
                    icon={<AttachMoneyOutlinedIcon />}
                    label="Fee"
                    value={
                      isMonthlyBilled
                        ? "Monthly billing"
                        : formik.values.feePerClass
                        ? `$${formik.values.feePerClass}`
                        : ""
                    }
                    color="#f59e0b"
                  />
                </Stack>

                {/* Schedule type badge */}
                <Box sx={{ mt: 3 }}>
                  <Chip
                    label={
                      SCHEDULE_TYPES.find(
                        (t) => t.value === formik.values.scheduleType
                      )?.label || "Regular"
                    }
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: 11,
                      bgcolor: alpha("#6366f1", 0.08),
                      color: "#6366f1",
                      textTransform: "capitalize",
                    }}
                  />
                </Box>

                {/* Readiness indicator */}
                {formValid && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      borderRadius: "10px",
                      bgcolor: alpha("#22c55e", 0.06),
                      border: `1px solid ${alpha("#22c55e", 0.12)}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 18, color: "#22c55e" }}
                    />
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 600, color: "#16a34a" }}
                    >
                      Ready to create schedule
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* ── FOOTER ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.5,
              px: 3,
              py: 2,
              borderTop: "1px solid rgba(0,0,0,0.06)",
              flexShrink: 0,
              bgcolor: "#fafbfc",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate("/class-schedule/list")}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => formik.handleSubmit()}
              disabled={!formValid || isLoading || isCheckingConflicts}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                px: 4,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.2)" },
              }}
            >
              {(isLoading || isCheckingConflicts) && (
                <CircularProgress
                  size={18}
                  sx={{ mr: 1, color: "inherit" }}
                />
              )}
              Create Schedule
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ════ CONFLICT DIALOG ════ */}
      <Dialog
        open={conflictDialogOpen}
        onClose={() => setConflictDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: 1300,
          "& .MuiPaper-root": { borderRadius: "14px" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
          }}
        >
          <WarningAmberIcon color="error" />
          Schedule Conflict Detected
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            The selected staff member or student is already booked at this time and cannot be assigned. Change the date/time or choose another suitable staff member.
          </Typography>
          <List disablePadding>
            {conflicts.map((conflict: any, index: number) => (
              <ListItem
                key={index}
                divider
                sx={{
                  borderRadius: "8px",
                  mb: 0.5,
                  bgcolor: alpha("#f59e0b", 0.04),
                }}
              >
                <ListItemText
                  primary={`${conflict.staffName || "Staff"} - ${conflict.scheduledDate || ""}`}
                  secondary={`${conflict.startTime || ""} - ${conflict.endTime || ""} | ${conflict.reason || "Time overlap"}`}
                  primaryTypographyProps={{
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                  secondaryTypographyProps={{ fontSize: 12 }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        {/* No "Create Anyway": a real staff/student conflict can never be overridden
            from the client, and the backend rejects any such attempt at save. */}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConflictDialogOpen(false)}
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => setConflictDialogOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
          >
            Change Date/Time or Staff
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Index;
